import React, { useState, useEffect, useRef, useCallback } from "react";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const ACTIVE_RE = /(?:^|[^\w@])@([a-z0-9_]{0,20})$/i;

function activeMention(value, caret) {
  const m = ACTIVE_RE.exec(value.slice(0, caret));
  if (!m) return null;
  return { q: m[1], start: caret - m[1].length - 1 };
}

// Controlled input/textarea with live @username search. Calls onChange({ target: { value } }).
export function MentionInput({ value, onChange, as = "input", inputRef, placement = "top", onKeyDown, className, ...rest }) {
  const localRef = useRef(null);
  const ref = inputRef || localRef;
  const [mention, setMention] = useState(null);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const Tag = as;

  const detect = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setMention(activeMention(el.value, el.selectionStart ?? el.value.length));
  }, [ref]);

  useEffect(() => {
    if (!mention) { setResults([]); return; }
    let dead = false;
    const t = setTimeout(() => {
      api.searchUsers(mention.q).then((rows) => { if (!dead) { setResults(rows.slice(0, 6)); setActive(0); } }).catch(() => {});
    }, 180);
    return () => { dead = true; clearTimeout(t); };
  }, [mention?.q, mention?.start]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (u) => {
    if (!mention) return;
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    const next = `${value.slice(0, mention.start)}@${u.username} ${value.slice(caret)}`;
    onChange({ target: { value: next } });
    setMention(null);
    const pos = mention.start + u.username.length + 2;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  };

  const keyDown = (e) => {
    if (mention && results.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % results.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(results[active]); return; }
      if (e.key === "Escape") { e.preventDefault(); setMention(null); return; }
    }
    onKeyDown && onKeyDown(e);
  };

  const open = mention && results.length > 0;
  return (
    <div className="relative min-w-0 flex-1">
      <Tag
        ref={ref}
        value={value}
        onChange={(e) => { onChange(e); requestAnimationFrame(detect); }}
        onKeyDown={keyDown}
        onKeyUp={detect}
        onClick={detect}
        onBlur={() => setTimeout(() => setMention(null), 150)}
        className={cn("w-full", className)}
        {...rest}
      />
      {open && (
        <div data-testid="mention-suggestions" role="listbox" className={cn("absolute left-0 z-50 w-full max-w-sm overflow-hidden rounded-2xl border border-am-border bg-am-surface2 shadow-2xl", placement === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5")}>
          {results.map((u, i) => (
            <button key={u.user_id} type="button" role="option" aria-selected={i === active} data-testid={`mention-option-${u.username}`}
              onMouseDown={(e) => { e.preventDefault(); pick(u); }} onMouseEnter={() => setActive(i)}
              className={cn("flex w-full items-center gap-2.5 px-3 py-2 text-left", i === active ? "bg-white/10" : "hover:bg-white/5")}>
              <AuraRing src={mediaUrl(u.picture) || undefined} size={30} ring={1.5} fallback={u.name} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1 truncate text-sm font-semibold text-white">{u.name}{u.is_verified && <VerifiedBadge size={12} />}</span>
                <span className="block truncate text-xs text-am-text2">@{u.username}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
